import { api } from './api'
import {
  ResearchStudy,
  ResearchStudyFormData,
  ResearchStudyEnrollment,
  EnrollmentFormData,
  ResearchStudyProtocol,
} from '../types/researchStudy'

export const researchStudyService = {
  /**
   * Get all research studies
   */
  getAll: async (): Promise<ResearchStudy[]> => {
    const response = await api.get<ResearchStudy[]>('/research-studies')
    return response.data
  },

  /**
   * Get a single research study by ID
   */
  getById: async (id: string): Promise<ResearchStudy> => {
    const response = await api.get<ResearchStudy>(`/research-studies/${id}`)
    return response.data
  },

  /**
   * Create a new research study
   */
  create: async (data: ResearchStudyFormData): Promise<ResearchStudy> => {
    const response = await api.post<ResearchStudy>('/research-studies', data)
    return response.data
  },

  /**
   * Update an existing research study
   */
  update: async (id: string, data: Partial<ResearchStudyFormData>): Promise<ResearchStudy> => {
    const response = await api.put<ResearchStudy>(`/research-studies/${id}`, data)
    return response.data
  },

  /**
   * Delete a research study
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/research-studies/${id}`)
  },

  /**
   * Get all studies for a patient
   */
  getPatientStudies: async (patientId: string): Promise<ResearchStudyEnrollment[]> => {
    const response = await api.get<ResearchStudyEnrollment[]>(`/patients/${patientId}/research-studies`)
    return response.data
  },

  /**
   * Enroll a patient in a study
   */
  enrollPatient: async (patientId: string, studyId: string, enrollmentData: Omit<EnrollmentFormData, 'studyId' | 'patientId'>): Promise<ResearchStudyEnrollment> => {
    const response = await api.post<ResearchStudyEnrollment>(`/patients/${patientId}/research-studies`, {
      studyId,
      ...enrollmentData,
    })
    return response.data
  },

  /**
   * Withdraw a patient from a study
   */
  withdrawPatient: async (patientId: string, studyId: string, reason?: string): Promise<ResearchStudyEnrollment> => {
    const response = await api.put<ResearchStudyEnrollment>(`/patients/${patientId}/research-studies/${studyId}/withdraw`, {
      reason,
    })
    return response.data
  },

  /**
   * Get study protocol
   */
  getStudyProtocol: async (studyId: string, version?: string): Promise<ResearchStudyProtocol> => {
    const url = version
      ? `/research-studies/${studyId}/protocol?version=${version}`
      : `/research-studies/${studyId}/protocol`
    const response = await api.get<ResearchStudyProtocol>(url)
    return response.data
  },

  /**
   * Create a new protocol version
   */
  createProtocolVersion: async (studyId: string, data: Omit<ResearchStudyProtocol, 'id' | 'version' | 'createdAt' | 'createdBy'>): Promise<ResearchStudyProtocol> => {
    const response = await api.post<ResearchStudyProtocol>(`/research-studies/${studyId}/protocol/versions`, data)
    return response.data
  },
}
